// SidebarComponent.js
import React, { useMemo, useState } from "react";
import {
    Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse, Tooltip, Checkbox
} from "@mui/material";
import {
    Apartment, ExpandLess, ExpandMore, MonitorHeart, Assessment, NotificationsActive, GitHub, Timeline, AccessAlarms,
    AccountCircle, Dataset, Block, Description, Home, Campaign, Terrain
} from "@mui/icons-material";
import { SIDEBAR_SENTINEL_KEY, useLayoutPreferences } from "components/LayoutPreferencesProvider";
import ListAltIcon from '@mui/icons-material/ListAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import KeyIcon from '@mui/icons-material/Key';
import { styled } from "@mui/material/styles";

const ICON_MAP = {
    Home: <Home fontSize="medium" />,
    MonitorHeart: <MonitorHeart fontSize="medium" />,
    Assessment: <Assessment fontSize="medium" />,
    NotificationsActive: <NotificationsActive fontSize="medium" />,
    GitHub: <GitHub fontSize="medium" />,
    Timeline: <Timeline fontSize="medium" />,
    AccessAlarms: <AccessAlarms fontSize="medium" />,
    AccountCircle: <AccountCircle fontSize="medium" />,
    Dataset: <Dataset fontSize="medium" />,
    Block: <Block fontSize="medium" />,
    ListAlt: <ListAltIcon fontSize="medium" />,
    Schedule: <ScheduleIcon fontSize="medium" />,
    EventAvailable: <EventAvailableIcon fontSize="medium" />,
    KeyIcon: <KeyIcon fontSize="medium" />,
    Description: <Description fontSize="medium" />,
    Campaign: <Campaign fontSize="medium" />,
    Terrain: <Terrain fontSize="medium" />,
    Apartment: <Apartment fontSize="medium" />
};

const StyledListItemText = styled(ListItemText)(({ selected }) => ({
    opacity: 1,
    color: '#FFFFFF',
    marginLeft: "0.5rem"
}));

const StyledMiniListItemIcon = styled(ListItemIcon)(({ selected }) => ({
    minWidth: 0,
    justifyContent: 'center',
    alignItems: "center",
    color: selected ? '#FFFFFF' : '#009FE3',
}));

const editCheckboxSx = {
    p: 0.5,
    mr: 0.5,
    color: '#FFFFFF',
    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#FFFFFF' },
};

export default function Sidebar({ menuData, open, selected, onClick, isSmallOrMediumScreen, onNavigate, homeRoute }) {
    const [expandedMenus, setExpandedMenus] = useState({});
    const {
        isEditing,
        sidebarDraft: draftKeys,
        toggleSidebarKeys: toggleDraftKeys,
        filterSidebarMenu,
    } = useLayoutPreferences();
    const safeMenuData = useMemo(() => (Array.isArray(menuData) ? menuData : []), [menuData]);
    const visibleMenu = useMemo(() => filterSidebarMenu(safeMenuData), [filterSidebarMenu, safeMenuData]);

    const homeItem = { key: SIDEBAR_SENTINEL_KEY, label: 'Home', icon: 'Home', intenalRoute: true, route: homeRoute };
    const editing = isEditing && open;

    const handleSubMenuToggle = (key) => {
        setExpandedMenus(prev => ({ ...prev, [key]: !prev[key] }));
        if (!isSmallOrMediumScreen) {
            onClick(key);
        }

    };

    const onSideNavItemSelect = (item) => {
        onClick(item.key);
        if (item.children && item.children.length > 0) {
            return;
        } else if (item.intenalRoute) {
            onNavigate(item.route);
        }
        else {
            window.open(item.route, "_blank");
        }
    }

    const renderEditMenuItem = item => {
        const hasSub = item.children && item.children.length > 0;
        const keys = hasSub ? item.children.map(child => child.key) : [item.key];
        const checkedCount = keys.filter(key => draftKeys.has(key)).length;
        const allChecked = checkedCount === keys.length;
        const someChecked = checkedCount > 0 && !allChecked;
        const toggle = () => toggleDraftKeys(keys, !allChecked);

        return (
            <React.Fragment key={item.key}>
                <ListItem disablePadding sx={{ opacity: checkedCount > 0 ? 1 : 0.5 }}>
                    <ListItemButton onClick={toggle}>
                        <Checkbox
                            size="small"
                            checked={allChecked}
                            indeterminate={someChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={toggle}
                            sx={editCheckboxSx}
                            inputProps={{ 'aria-label': `Show ${item.label} in menu` }}
                        />
                        <StyledMiniListItemIcon>{ICON_MAP[item.icon]}</StyledMiniListItemIcon>
                        <StyledListItemText primary={item.label} />
                    </ListItemButton>
                </ListItem>
                {hasSub && (
                    <List component="div" disablePadding>
                        {item.children.map(sub => {
                            const isChecked = draftKeys.has(sub.key);
                            const toggleSub = () => toggleDraftKeys([sub.key], !isChecked);
                            return (
                                <ListItem key={sub.key} disablePadding sx={{ opacity: isChecked ? 1 : 0.5 }}>
                                    <ListItemButton sx={{ pl: 4 }} onClick={toggleSub}>
                                        <Checkbox
                                            size="small"
                                            checked={isChecked}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={toggleSub}
                                            sx={editCheckboxSx}
                                            inputProps={{ 'aria-label': `Show ${sub.label} in menu` }}
                                        />
                                        <StyledListItemText primary={sub.label} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </React.Fragment>
        );
    };

    const renderMenuItem = item => {
        const hasSub = item.children && item.children.length > 0;
        const isOpen = expandedMenus[item.key];
        const isSelected = selected === item.key;
        if (!open) {
            return (
                <ListItem key={item.key} disablePadding>
                    <Tooltip title={item.label} placement="right">
                        <ListItemButton onClick={() => onSideNavItemSelect(item)}>
                            <StyledMiniListItemIcon selected={isSelected}>{ICON_MAP[item.icon]}</StyledMiniListItemIcon>
                        </ListItemButton>
                    </Tooltip>
                </ListItem>
            );
        }
        return (
            <React.Fragment key={item.key}>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => hasSub ? handleSubMenuToggle(item.key) : onSideNavItemSelect(item)}>
                        <StyledMiniListItemIcon selected={isSelected}>{ICON_MAP[item.icon]}</StyledMiniListItemIcon>
                        <StyledListItemText primary={item.label} selected={isSelected} />
                        {hasSub && (isOpen ? <ExpandLess style={{ fill: "#ffffff" }} /> : <ExpandMore style={{ fill: "#ffffff" }} />)}
                    </ListItemButton>
                </ListItem>
                {hasSub && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.children.map(sub => (
                                <ListItem key={sub.key} disablePadding>
                                    <ListItemButton sx={{ pl: 4 }} onClick={() => { onSideNavItemSelect(sub); onClick(item.key) }}>
                                        <StyledListItemText selected={isSelected} primary={sub.label} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    return (
        <Box>
            <List>
                {editing ? (
                    <>
                        {renderMenuItem(homeItem)}
                        {safeMenuData.map(item => renderEditMenuItem(item))}
                    </>
                ) : (
                    [homeItem, ...visibleMenu].map(item => renderMenuItem(item))
                )}
            </List>
        </Box>
    );
}
